import { useState } from 'react';
import {
  Box, Typography, Paper, TextField, Grid, Button, Container,
  MenuItem, Alert, CircularProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useColors } from '../context/ThemeContext';
import api from '../api/client';

const STATUS_OPTIONS = [
  'Artiste indépendant',
  'Auteur / Compositeur',
  'Label',
  'Producteur',
  'Éditeur musical',
  'Manager',
  'Autre',
];

export default function Contact() {
  const colors = useColors();
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    status: '',
    phone: '',
    message: '',
  });
  const [phoneError, setPhoneError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const isPhoneValid = (value) => /^(\+?\d{1,3}[\s.-]?)?(\(?\d{1,4}\)?[\s.-]?)?\d[\d\s.-]{6,14}$/.test(value.trim());
  const isEmailValid = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

  const allFilled = form.firstName.trim() && form.lastName.trim()
    && form.email.trim() && form.status && form.phone.trim() && form.message.trim()
    && !phoneError && !emailError;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!allFilled) return;
    setLoading(true);
    setError('');
    try {
      await api.post('/auth/contact/', {
        first_name: form.firstName,
        last_name: form.lastName,
        email: form.email,
        status: form.status,
        phone: form.phone,
        message: form.message,
      });
      setSuccess(true);
    } catch (err) {
      const msg = err?.response?.data?.error || 'Une erreur est survenue. Veuillez réessayer.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Box sx={{ bgcolor: colors.bgDefault, minHeight: 'calc(100vh - 64px)' }}>
        <Container maxWidth="md" sx={{ py: { xs: 6, md: 10 }, textAlign: 'center' }}>
          <CheckCircleIcon sx={{ fontSize: 64, color: colors.success, mb: 2 }} />
          <Typography variant="h4" sx={{ color: colors.textPrimary, fontWeight: 700, mb: 2 }}>
            Message envoyé !
          </Typography>
          <Typography variant="body1" sx={{ color: colors.textSecondary, maxWidth: 500, mx: 'auto' }}>
            Merci pour votre message. Nous reviendrons vers vous dans les plus brefs délais.
          </Typography>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: colors.bgDefault, minHeight: 'calc(100vh - 64px)' }}>
      <Container maxWidth="md" sx={{ py: { xs: 4, md: 8 } }}>
        <Typography
          variant="h3"
          sx={{
            textAlign: 'center',
            fontWeight: 700,
            mb: 2,
            color: colors.textPrimary,
            fontSize: { xs: '2rem', md: '2.5rem' },
          }}
        >
          Nous contacter
        </Typography>
        <Typography
          variant="body1"
          sx={{ textAlign: 'center', color: colors.textSecondary, mb: 6, maxWidth: '700px', mx: 'auto' }}
        >
          Pour l'offre Business ou toute autre question, laissez-nous vos coordonnées et nous reviendrons vers vous rapidement.
        </Typography>

        <Paper
          elevation={0}
          sx={{
            maxWidth: 820,
            mx: 'auto',
            p: { xs: 3, md: 5 },
            bgcolor: colors.bgPaper,
            border: `1px solid ${colors.border}`,
            borderRadius: 3,
          }}
          component="form"
          onSubmit={handleSubmit}
        >
          <Grid container spacing={2.5}>
            <Grid item xs={12} md={6}>
              <TextField label="Nom" fullWidth required value={form.lastName}
                onChange={(e) => setForm({ ...form, lastName: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label="Prénom" fullWidth required value={form.firstName}
                onChange={(e) => setForm({ ...form, firstName: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Adresse email"
                fullWidth
                required
                type="email"
                value={form.email}
                onChange={(e) => {
                  setForm({ ...form, email: e.target.value });
                  if (emailError) setEmailError('');
                }}
                onBlur={() => {
                  if (form.email.trim() && !isEmailValid(form.email)) {
                    setEmailError('Format invalide (ex: nom@exemple.com)');
                  }
                }}
                error={!!emailError}
                helperText={emailError}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Condition / activité"
                fullWidth
                required
                select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Numéro de téléphone"
                fullWidth
                required
                type="tel"
                inputProps={{ inputMode: 'tel' }}
                value={form.phone}
                onChange={(e) => {
                  setForm({ ...form, phone: e.target.value });
                  if (phoneError) setPhoneError('');
                }}
                onBlur={() => {
                  if (form.phone.trim() && !isPhoneValid(form.phone)) {
                    setPhoneError('Format invalide (ex: 06 12 34 56 78 ou +33 6 12 34 56 78)');
                  }
                }}
                error={!!phoneError}
                helperText={phoneError}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Votre message" fullWidth required multiline minRows={5}
                value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
              <Button
                type="submit"
                variant="contained"
                disabled={!allFilled || loading}
                sx={{
                  bgcolor: colors.primaryLight,
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  '&:hover': { bgcolor: colors.primaryLight },
                  '&:disabled': { bgcolor: colors.bgMuted, color: colors.textMuted },
                }}
              >
                {loading ? <CircularProgress size={22} sx={{ color: 'white' }} /> : 'Envoyer'}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Container>
    </Box>
  );
}
